export function generateTable(
  pascal: string,
  kebab: string,
  label: string,
  actionImportPath: string
): string {
  return `'use client'

import type { ${pascal}SubmissionData } from '@admin/actions/${actionImportPath}'
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHead,
  DataGridHeader,
  DataGridRow
} from '@admin/components/shared/data-table/data-grid'
import { DataTablePagination } from '@admin/components/shared/data-table/data-table-pagination'
import { use${pascal}Submissions } from '@admin/hooks/use-${kebab}-form'
import { useTableUtils } from '@admin/hooks/use-table-utils'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table'
import { GripVertical } from 'lucide-react'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
import * as React from 'react'
import { cn } from '@admin/utils/shared/cn'

interface ${pascal}SubmissionsTableProps<TValue> extends React.HTMLAttributes<HTMLDivElement> {
  columns: ColumnDef<${pascal}SubmissionData, TValue>[]
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  onViewSubmission: (id: string) => void
  search?: string
  submittedAtFrom?: string
  submittedAtTo?: string
}

export function ${pascal}SubmissionsTable<TValue>({
  columns,
  selectedIds,
  setSelectedIds,
  onViewSubmission,
  search,
  submittedAtFrom,
  submittedAtTo,
  className
}: ${pascal}SubmissionsTableProps<TValue>) {
  const defaultPageSize = 20
  const [{
    page: pageIndex,
    size: pageSize,
    sort: sortField,
    dir: sortDir
  }, setTableQuery] = useQueryStates({
    page: parseAsInteger.withDefault(0),
    size: parseAsInteger.withDefault(defaultPageSize),
    sort: parseAsString.withDefault(''),
    dir: parseAsString.withDefault('')
  })

  const safePageIndex = Number.isFinite(pageIndex) && pageIndex > 0 ? pageIndex : 0
  const safePageSize = pageSize === -1 || (Number.isFinite(pageSize) && pageSize > 0) ? pageSize : defaultPageSize

  const { data, error, isPending } = use${pascal}Submissions(
    search,
    submittedAtFrom,
    submittedAtTo,
    safePageIndex,
    safePageSize,
    sortField || undefined,
    (sortDir as 'asc' | 'desc') || undefined
  )

  const tableData = data?.submissions ?? []
  const totalRows = data?.total ?? 0
  const effectivePageSize = safePageSize === -1 ? totalRows : safePageSize
  const pageCount = effectivePageSize > 0 ? Math.ceil(totalRows / effectivePageSize) : 1
  const shouldShowPagination = totalRows > defaultPageSize

  const {
    columnSizing,
    handleColumnSizingChange,
    handlePaginationChange,
    handleRowSelectionChange,
    handleSortingChange,
    hoveredColumnId,
    registerTable,
    resetColumnSizingToContainer,
    rowSelection,
    setHoveredColumnId,
    sorting,
    tableContainerRef
  } = useTableUtils<${pascal}SubmissionData>({
    data: tableData,
    selectedIds,
    onSelectedIdsChange: setSelectedIds,
    pageIndex: safePageIndex,
    pageSize: effectivePageSize,
    setPageIndex: (nextPageIndex) => setTableQuery({ page: nextPageIndex }),
    setSortingParams: ({
      pageIndex: nextPageIndex,
      sortDir: nextSortDir,
      sortField: nextSortField
    }) => setTableQuery({
      page: nextPageIndex,
      sort: nextSortField,
      dir: nextSortDir
    }),
    sortField,
    sortDir
  })

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 48,
      size: 220
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    rowCount: totalRows,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnSizing,
      rowSelection,
      pagination: {
        pageIndex: safePageIndex,
        pageSize: effectivePageSize
      }
    }
  })
  registerTable(table)
  const gridTemplateColumns = table
    .getVisibleLeafColumns()
    .map((column) => \`\${column.getSize()}px\`)
    .join(' ')

  return (
    <div className={cn("flex flex-col gap-6 pt-px pb-2 h-full", className)}>
      <DataGrid containerClassName="min-h-0 flex-1" containerRef={tableContainerRef} gridTemplateColumns={gridTemplateColumns} style={{ width: table.getTotalSize() }}>
      <DataGridHeader>
        {table.getHeaderGroups().map((headerGroup) => (
          <DataGridRow key={headerGroup.id}>
            {headerGroup.headers.map((header, headerIndex) => {
              const meta = header.column.columnDef.meta as Record<string, unknown> | undefined
              const resizeLabel = typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : header.column.id
              const nextHeader = headerGroup.headers[headerIndex + 1]
              const canResizeColumn = header.column.getCanResize() && nextHeader?.column.getCanResize() === true
              return (
                <DataGridHead
                  key={header.id}
                  className={cn('group/resize-header relative overflow-hidden', {
                    'text-center': meta?.align === 'center',
                    'text-right': meta?.align === 'right'
                  })}
                  data-column-id={header.column.id}
                  onMouseEnter={() => setHoveredColumnId(header.column.id)}
                  onMouseLeave={() => setHoveredColumnId((current) => (current === header.column.id ? null : current))}
                >
                  {header.isPlaceholder
                    ? null
                    : flexRender(header.column.columnDef.header, header.getContext())}
                  {canResizeColumn ? (
                    <button
                      type="button"
                      aria-label={\`Resize \${resizeLabel} column\`}
                      className="group/resize-handle absolute right-0 top-0 z-10 flex h-full w-5 cursor-col-resize touch-none select-none items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:text-foreground focus-visible:opacity-100 focus-visible:outline-none group-focus-within/resize-header:opacity-100 data-[column-hovered=true]:opacity-100 data-[resizing=true]:opacity-100"
                      data-resize-handle
                      data-column-hovered={hoveredColumnId === header.column.id ? 'true' : 'false'}
                      data-resizing={header.column.getIsResizing() ? 'true' : 'false'}
                      onDoubleClick={resetColumnSizingToContainer}
                      onMouseDown={header.getResizeHandler()}
                      onTouchStart={header.getResizeHandler()}
                    >
                      <GripVertical aria-hidden="true" className={cn('size-3.5', { 'text-ring': header.column.getIsResizing() })} />
                    </button>
                  ) : null}
                </DataGridHead>
              )
            })}
          </DataGridRow>
        ))}
      </DataGridHeader>
      <DataGridBody>
        {isPending ? (
          <DataGridRow>
            <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
              <div className="text-muted-foreground">Loading ${label} submissions…</div>
            </DataGridCell>
          </DataGridRow>
        ) : error ? (
          <DataGridRow>
            <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
              <div className="text-destructive">Error loading ${label} submissions: {error.message}</div>
            </DataGridCell>
          </DataGridRow>
        ) : table.getRowModel().rows?.length ? (
          table.getRowModel().rows.map((row) => (
            <DataGridRow
              key={row.id}
              data-state={row.getIsSelected() ? 'selected' : undefined}
              className="cursor-pointer hover:bg-muted/50"
              onClick={(e) => {
                const target = e.target as HTMLElement
                if (!e.currentTarget.contains(target)) return
                if (target.closest('button, a, input, [role="checkbox"]')) return
                onViewSubmission(row.original.id)
              }}
            >
              {row.getVisibleCells().map((cell) => {
                const meta = cell.column.columnDef.meta as Record<string, unknown> | undefined
                return (
                  <DataGridCell
                    key={cell.id}
                    className={cn({
                      'text-center': meta?.align === 'center',
                      'text-right': meta?.align === 'right'
                    })}
                    data-column-id={cell.column.id}
                    onMouseEnter={() => setHoveredColumnId(cell.column.id)}
                    onMouseLeave={() => setHoveredColumnId((current) => (current === cell.column.id ? null : current))}
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </DataGridCell>
                )
              })}
            </DataGridRow>
          ))
        ) : (
          <DataGridRow>
            <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
              No ${label} submissions found.
            </DataGridCell>
          </DataGridRow>
        )}
      </DataGridBody>
      </DataGrid>

      {shouldShowPagination ? (
        <DataTablePagination
          table={table}
          paginationMode="controlled"
          pageSize={safePageSize}
          pageIndex={safePageIndex}
          pageCount={pageCount}
          setPageIndex={(nextPageIndex) => setTableQuery({ page: nextPageIndex })}
          setPageSize={(nextPageSize) =>
            setTableQuery({ page: 0, size: nextPageSize })
          }
        />
      ) : null}
    </div>
  )
}
`
}
